import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { PaginationLimit } from 'src/app/core/constants/dropdown-contants';
import { CacheBoardNameService } from './services/cache-board-name.service';

@Component({
  selector: 'app-board-name',
  templateUrl: './board-name.component.html',
  styleUrls: ['./board-name.component.scss']
})
export class BoardNameComponent implements OnInit, OnChanges {
  @Input() control: FormControl = new FormControl(null);
  @Input() label: string = 'Select Board Name';
  @Input() placeholder: string = 'Select board';
  @Input() selectedBoard: string = '';
  @Input() boardType: string = '';
  @Input() showAllBoards: boolean = false;
  @Input() multiple: boolean = false;
  @Input() showClear?: boolean = false;
  @Output() search: EventEmitter<string> = new EventEmitter<string>();
  @Output() nextOffsetBoard: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() boardDetails: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectionChange: EventEmitter<any> = new EventEmitter<any>();
  

  boardList: any[] = [];
  paginationLimit: number = PaginationLimit.board;
  totalBoardCount: number = 0;
  boardOffset: number = PaginationLimit.board;
  boardPagination = {
    skip: 0,
    limit: PaginationLimit.board
  };
  boardFilter: FormControl = new FormControl('');
  isLoading: boolean = false;
  searchValue: BehaviorSubject<string> = new BehaviorSubject('');

  constructor(
   private _cacheBoardService: CacheBoardNameService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedBoard']?.currentValue) {
      if (this.boardList.length === 0) {
        this.getBoards();
      } else if (this.multiple) {
        const selectedIds = Array.isArray(this.selectedBoard) ? this.selectedBoard : [this.selectedBoard];
        selectedIds.forEach(id => {
          if (!this.boardList.some(b => b.id === id)) {
            this.checkBoardExists(id);
          }
        });
      } else {
        this.checkBoardExists(changes['selectedBoard'].currentValue);
      }
    }
  }

  ngOnInit() {
    this.getBoards();
  }

  async getBoards() {
    await this.loadBoards();
    if (this.selectedBoard) {
      this.checkBoardExists(this.selectedBoard);
    }
  }

  private async loadBoards(filter: string = ''): Promise<void> {
    if (filter) {
      this.boardPagination = {
        skip: 0,
        limit: PaginationLimit.board
      };
      this.boardList = [];
    }

    try {
      this.isLoading = true;
      // Fetch boards using the cache service
      const boardData: { data: any[], total: number } = await this._cacheBoardService.fetchBoards(
        this.boardPagination,
        filter
      );

      // Map the board data to the required format
      const boardList = boardData.data.map((v: any) => ({
        name: v?.boardName || '',  // Adjust these fields based on your actual board data structure
        id: v?.id || ''
      }));

      // Append new boards to existing list
      this.boardList = [...this.boardList, ...boardList];

      // Add "All boards" option if showAllBoards is true
      if (this.showAllBoards) {
        this.boardList.unshift({
          id: '',
          name: `All ${this.boardType || 'Boards'}`
        });
      }

      // Remove duplicates
      this.boardList = [...new Map(this.boardList.map(item => [item.id, item])).values()];

      // Update total count
      this.totalBoardCount = boardData.total;

    } catch (error) {
      console.error('Error loading boards:', error);
    } finally {
      this.isLoading = false;
    }
}

  private checkBoardExists(boardId: string): void {
    if (boardId && !this.boardList.some((b) => b.id === boardId)) {
      this._cacheBoardService.getBoardById(boardId).subscribe({
        next: (board) => {
          if (board && board?.statusCode === 200 && board?.data) {
            if (!this.boardList.some(b => b.id === boardId)) {
              this.boardList.push({
                name: board.data[0].boardName,
                id: board.data[0].id
              });
              this.boardList = [...this.boardList];
            }
          } else {
            console.warn(`Board with ID ${boardId} not found.`);
          }
        },
        error: (err) => console.error('Error fetching board by ID:', err)
      });
    }
  }

  getNextBoard(): void {
    if (this.searchValue?.value) return;
    if (this.boardPagination.skip < this.totalBoardCount) {
      this.boardPagination.skip += this.paginationLimit;
      this.loadBoards(this.boardFilter.value);
    }
  }

  async filterBoards(filter: string) {
    this.searchValue.next(filter);
    this.boardList = [];
    await this.loadBoards(filter);
    if (this.control.value) {
      this.checkBoardExists(this.control.value);
    }
  }

  onSelectionChange(event: any) {
    this.selectionChange.emit(event);
  }
}